import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { Button, Input, Select, CheckboxGroup, Avatar, Badge, getRoleBadgeVariant } from '@/components/common';
import { usersApi } from '@/lib/api';
import { cn } from '@/lib/utils';
import type { UpdateUserRequest, DayOfWeek, ApiError } from '@/types';
import { usePushNotifications } from '@/hooks/usePushNotifications';

const dayOptions = [
  { value: 'monday', label: 'Mon' },
  { value: 'tuesday', label: 'Tue' },
  { value: 'wednesday', label: 'Wed' },
  { value: 'thursday', label: 'Thu' },
  { value: 'friday', label: 'Fri' },
  { value: 'saturday', label: 'Sat' },
  { value: 'sunday', label: 'Sun' },
];

export function SettingsPage() {
  const { user, isAuthenticated, isLoading: authLoading, refreshUser } = useAuth();
  const navigate = useNavigate();

  const [formData, setFormData] = useState<UpdateUserRequest>({
    first_name: '',
    last_name: '',
    other_role_label: '',
    hourly_wage: undefined,
    max_hours_per_month: undefined,
    preferred_days_off: [],
    unavailable_days: [],
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Initialize form data from user
  useEffect(() => {
    if (user) {
      setFormData({
        first_name: user.first_name,
        last_name: user.last_name || '',
        other_role_label: user.other_role_label || '',
        hourly_wage: user.hourly_wage || undefined,
        max_hours_per_month: user.max_hours_per_month || undefined,
        preferred_days_off: user.preferred_days_off || [],
        unavailable_days: user.unavailable_days || [],
      });
    }
  }, [user]);

  // Redirect to login if not authenticated
  useEffect(() => {
    if (!isAuthenticated && !authLoading) {
      navigate('/', { replace: true });
    }
  }, [isAuthenticated, authLoading, navigate]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value, type } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'number' ? (value ? parseFloat(value) : undefined) : value,
    }));
    setError(null);
    setSuccess(null);
  };

  const handleDaysChange = (field: 'preferred_days_off' | 'unavailable_days') => (value: string[]) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value as DayOfWeek[],
    }));
    setError(null);
    setSuccess(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;

    setIsLoading(true);
    setError(null);
    setSuccess(null);

    try {
      await usersApi.update(user.id, formData);
      await refreshUser();
      setSuccess('Settings updated successfully');
    } catch (err) {
      const apiError = err as ApiError;
      setError(apiError.message || 'Failed to update settings');
    } finally {
      setIsLoading(false);
    }
  };

  const handleProfilePictureChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!user || !e.target.files?.[0]) return;

    const file = e.target.files[0];

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setError('Please select an image file');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setError('Image must be less than 5MB');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      await usersApi.uploadProfilePicture(user.id, file);
      await refreshUser();
      setSuccess('Profile picture updated successfully');
    } catch (err) {
      const apiError = err as ApiError;
      setError(apiError.message || 'Failed to upload profile picture');
    } finally {
      setIsLoading(false);
    }
  };

  // Loading state
  if (authLoading || !user) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <svg
            className="animate-spin h-8 w-8 text-accent"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            />
          </svg>
          <p className="text-gray-400">Loading...</p>
        </div>
      </div>
    );
  }

  const { isSupported: pushSupported, isSubscribed: pushSubscribed, isLoading: pushLoading, subscribe: pushSubscribe, unsubscribe: pushUnsubscribe } = usePushNotifications();
  const isCaretaker = user.role === 'caretaker';
  const isProfessional = user.role === 'professional';

  const getDisplayRole = (role: string) => {
    switch (role) {
      case 'parent': return 'Guardian';
      case 'caretaker': return 'Caretaker';
      case 'family': return 'Family';
      case 'professional': return 'Professional';
      default: return role.charAt(0).toUpperCase() + role.slice(1);
    }
  };

  const displayRole = user.role === 'professional' && user.other_role_label
    ? user.other_role_label
    : getDisplayRole(user.role);

  return (
    <div data-testid="container-settings-page" className="min-h-screen bg-background py-8 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Back to dashboard */}
        <Link
          to="/dashboard"
          className="inline-flex items-center gap-2 text-gray-400 hover:text-white mb-6 transition-colors"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
          </svg>
          Back to Dashboard
        </Link>

        <div className="bg-card rounded-xl border border-slate-700 shadow-lg overflow-hidden">
          {/* Header */}
          <div className="px-6 py-8 border-b border-slate-700 bg-gradient-to-r from-slate-800 to-slate-700">
            <div className="flex items-center gap-6">
              {/* Profile picture */}
              <div className="relative group">
                <Avatar
                  firstName={user.first_name}
                  lastName={user.last_name}
                  profilePictureUrl={user.profile_picture_url}
                  role={user.role}
                  size="xl"
                />
                <label data-testid="input-settings-avatar-upload" className="absolute inset-0 flex items-center justify-center bg-black/50 opacity-0 group-hover:opacity-100 rounded-full cursor-pointer transition-opacity">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleProfilePictureChange}
                    className="sr-only"
                  />
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                </label>
              </div>

              <div>
                <h1 className="text-2xl font-bold text-white">
                  {user.first_name} {user.last_name || ''}
                </h1>
                <p className="text-gray-400">{user.email}</p>
                <div className="mt-2">
                  <Badge variant={getRoleBadgeVariant(user.role)}>
                    {displayRole}
                  </Badge>
                </div>
              </div>
            </div>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="p-6 space-y-6">
            {/* Messages */}
            {error && (
              <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/30 text-red-400 text-sm">
                {error}
              </div>
            )}
            {success && (
              <div className="p-3 rounded-lg bg-green-500/10 border border-green-500/30 text-green-400 text-sm">
                {success}
              </div>
            )}

            {/* Name fields */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="First Name"
                type="text"
                name="first_name"
                value={formData.first_name || ''}
                onChange={handleChange}
                placeholder="Your first name"
                required
                data-testid="input-settings-first-name"
              />
              <Input
                label="Last Name"
                type="text"
                name="last_name"
                value={formData.last_name || ''}
                onChange={handleChange}
                placeholder="Your last name"
                data-testid="input-settings-last-name"
              />
            </div>

            {/* Professional occupation label */}
            {isProfessional && (
              <Input
                label="Occupation"
                type="text"
                name="other_role_label"
                value={formData.other_role_label || ''}
                onChange={handleChange}
                placeholder="e.g., Nanny, Tutor, Therapist"
                data-testid="input-settings-occupation"
              />
            )}

            {/* Caretaker-specific fields */}
            {isCaretaker && (
              <>
                <div className="border-t border-slate-700 pt-6">
                  <h3 className="text-lg font-semibold text-white mb-4">Caretaker Settings</h3>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                    <Input
                      label="Hourly Wage ($)"
                      type="number"
                      name="hourly_wage"
                      value={formData.hourly_wage || ''}
                      onChange={handleChange}
                      placeholder="e.g., 25.00"
                      min="0"
                      step="0.01"
                      data-testid="input-settings-hourly-wage"
                    />
                    <Input
                      label="Max Hours/Month"
                      type="number"
                      name="max_hours_per_month"
                      value={formData.max_hours_per_month || ''}
                      onChange={handleChange}
                      placeholder="e.g., 40"
                      min="0"
                      data-testid="input-settings-max-hours"
                    />
                  </div>

                  <div className="space-y-4">
                    <CheckboxGroup
                      label="Preferred Days Off"
                      name="settings-dayoff"
                      options={dayOptions}
                      value={formData.preferred_days_off || []}
                      onChange={handleDaysChange('preferred_days_off')}
                      columns={7}
                    />

                    <CheckboxGroup
                      label="Days Cannot Work"
                      name="settings-unavailable"
                      options={dayOptions}
                      value={formData.unavailable_days || []}
                      onChange={handleDaysChange('unavailable_days')}
                      columns={7}
                    />
                  </div>
                </div>
              </>
            )}

            {/* Push Notifications */}
            {pushSupported && (
              <div className="border-t border-slate-700 pt-6">
                <h3 className="text-lg font-semibold text-white mb-4">Notifications</h3>
                <div className="flex items-center justify-between p-4 bg-slate-800 rounded-lg">
                  <div>
                    <p className="text-white font-medium">Push Notifications</p>
                    <p className="text-gray-400 text-sm mt-0.5">
                      {pushSubscribed
                        ? 'You will receive push notifications for mentions, shift assignments, and reminders.'
                        : 'Enable to get notified about mentions, shift changes, and upcoming shifts.'}
                    </p>
                  </div>
                  <button
                    onClick={() => pushSubscribed ? pushUnsubscribe() : pushSubscribe()}
                    disabled={pushLoading}
                    data-testid="clickable-push-toggle"
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                      pushSubscribed ? 'bg-blue-500' : 'bg-slate-600'
                    } ${pushLoading ? 'opacity-50' : ''}`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        pushSubscribed ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </button>
                </div>
              </div>
            )}

            {/* Submit button */}
            <div className="flex justify-end pt-4 border-t border-slate-700">
              <Button
                type="submit"
                variant="primary"
                size="lg"
                isLoading={isLoading}
                data-testid="clickable-settings-save"
              >
                Save Changes
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default SettingsPage;
